import PropTypes from 'prop-types';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../../../context/I18nContext';
import { RoomProvider, useRoomContext } from '../../../../context/RoomContext';
import { useModal } from '../../../../context/ModalContext';
import { generateThumbnail } from '../../../../utils/thumbnailGenerator';
import { getMatrixMessageType } from '../../../../utils/fileUtils';
import { MAX_FILE_SIZE_MB } from '../../../../constants';
import useTypingIndicator from '../../../../hooks/useTypingIndicator';
import useSendTypingIndicator from '../../../../hooks/useSendTypingIndicator';
import useRoomEdit from '../../../../hooks/useRoomEdit';
import useReadReceipts from '../../../../hooks/useReadReceipts';
import { useResponsiveWidth } from '../../../../hooks/useResponsiveWidth';
import { useRoomProfile } from '../../../../hooks/useRoomProfile';
import { useVisibleMessages } from '../../../../hooks/useVisibleMessages';
import { MessageDraftsProvider, useMessageDraftsContext } from '../../../../context/MessageDraftsContext';
import logger from '../../../../utils/logger';
import TopBar from './TopBar';
import Messages from './Messages/Messages';
import SendAttachmentModal from './SendAttachmentModal';
import Details from './Details';
import ReactionDetails from './ReactionDetails';
import MessageDetails from '../../../RightSidebar/MessageDetails';
import EditHistory from '../../../RightSidebar/EditHistory';
import ContactsSelector from './ContactsSelector';
import ContactDetailsSidebar from '../../../RightSidebar/ContactDetailsSidebar';
import RoomSettings from './RoomSettings';
import { useRoomSettings } from '../../../../hooks/useRoomSettings';
import './ChatView.css';

const ChatViewContent = ({
  isEditing = false,
  startEdit = () => {},
  closeEdit = () => {},
  startUpload = () => {},
  stopUpload = () => {},
  isUploading = false,
  uploadProgress = -1,
  setUploadProgress = () => {},
  onCancelUpload = () => {},
  attachment = null,
  onTriggerFileSelect = () => {},
  onClearAttachment = () => {},
  onLeaveRoom = null,
  isScreenWideEnoughForDetails = true,
}) => {
  // --- Component UI State ---
  const [isDetailsPanelOpen, setDetailsPanelOpen] = useState(true);
  const [isReactionDetailsOpen, setIsReactionDetailsOpen] = useState(false);
  const [isContactDetailsOpen, setIsContactDetailsOpen] = useState(false);
  const [isMessageDetailsOpen, setIsMessageDetailsOpen] = useState(false);
  const [isEditHistoryOpen, setIsEditHistoryOpen] = useState(false);
  const [isRoomSettingsOpen, setIsRoomSettingsOpen] = useState(false);
  const [editHistoryMessage, setEditHistoryMessage] = useState(null);
  const [currentContactId, setCurrentContactId] = useState(null);
  const [currentMessageData, setCurrentMessageData] = useState(null);
  const [reactionDetailsData, setReactionDetailsData] = useState(null);
  const [attachmentThumbnailBlob, setAttachmentThumbnailBlob] = useState(null);
  const [attachmentThumbnailResult, setAttachmentThumbnailResult] = useState(null);
  const [attachmentMetadata, setAttachmentMetadata] = useState(null);
  const [isGeneratingThumbnail, setIsGeneratingThumbnail] = useState(false);
  const thumbnailAbortController = useRef(null);
  const messagesScrollRef = useRef(null);

  // --- Contexts & Core Hooks ---
  const { t } = useI18n();
  const { showError, showConfirm } = useModal();
  const navigate = useNavigate();

  // --- View Mode Data ---
  const {
    client,
    name,
    topic,
    roomId,
    members,
    roomAvatars,
    messageGroups,
    isRoomLoading,
    roomExists,
    isEncrypted,
    leaveRoom,
    sendMessage,
    sendAttachment,
    deleteMessage,
    // Timeline pagination controls
    isLoadingOlder,
    isLoadingNewer,
    canPaginateBackward,
    canPaginateForward,
    windowPosition,
    isFillingViewport,
    paginateBackward,
    paginateForward,
    readMarkerId,
    setReadMarker,
    // Unread tracking
    unreadCount,
  } = useRoomContext();

  // Consume the new MessageDraftsContext
  const {
    replyingTo, cancelReply, currentMessage, onSetMessage,
  } = useMessageDraftsContext();
  const { onMessageSent } = useSendTypingIndicator(roomId);
  const { typingText } = useTypingIndicator(roomId);
  const { uploadRoomAvatar } = useRoomProfile(roomId);

  // --- Responsive Width Hooks ---
  // Track ChatView width to auto-hide DetailsPanel below 620px
  const { ref: chatViewRef, isWideEnough: isChatViewWideEnough } = useResponsiveWidth(620);
  
  // Combine both conditions - hide if EITHER ChatView is too narrow OR screen is too small
  // isScreenWideEnoughForDetails comes from parent (ConversationsPage tracks 700px breakpoint)
  const isWideEnoughForDetails = isChatViewWideEnough && isScreenWideEnoughForDetails;

  // --- Read Receipts (Netsfere approach) ---
  // Track which messages are actually visible in the viewport using getBoundingClientRect
  // This ensures read receipts are only sent for messages the user can actually see
  const { visibleEventIds, getLastVisibleEventId } = useVisibleMessages(messagesScrollRef, messageGroups);

  // Send read receipts for visible messages (only in view mode, not edit mode)
  useReadReceipts(roomId, visibleEventIds, !isEditing, getLastVisibleEventId);

  // --- Edit Mode State & Logic ---
  const {
    selectedContacts,
    resetToOriginal,
    toggleContact,
    removeContact,
    saveRoomEdits,
    getNewInvites,
    getDefaultTitle,
    canInvite,
    canRename,
    canRemove,
    canSetRoomAvatar,
    editedTopic,
    setEditedTopic,
    // Avatar state - computed automatically from selected members
    selectedAvatarOption,
    selectedAvatarFile,
    avatarUrls,
    defaultAvatarUrls, // Member avatars for modal display
    hasAvatarChanged, // Whether avatar has been modified from original
    // Avatar selection functions
    selectDefaultAvatar,
    selectPresetAvatar,
    selectCustomAvatar,
    // External users (cached users not in contact list)
    externalUsers,
  } = useRoomEdit({ 
    existingRoomId: roomId, // Always pass roomId to get permissions
    initialAvatarOption: 'default', // Start with default (member avatars)
  });

  // User can access edit UI if they have at least one permission
  const canEdit = canInvite || canRename || canRemove || canSetRoomAvatar;

  // Get room settings permissions
  const { canConfigure } = useRoomSettings(roomId);

  const [editedTitle, setEditedTitle] = useState(name);

  // --- Thumbnail Generation ---

  const generateAttachmentThumbnail = useCallback(async (file) => {
    // Cancel any ongoing thumbnail generation
    if (thumbnailAbortController.current) {
      thumbnailAbortController.current.abort();
    }

    setIsGeneratingThumbnail(true);
    if (!file) {
      setAttachmentThumbnailBlob(null);
      setAttachmentThumbnailResult(null);
      setAttachmentMetadata(null);
      thumbnailAbortController.current = null;
      return;
    }

    // Create new abort controller for this generation
    const controller = new AbortController();
    thumbnailAbortController.current = controller;

    try {
      // Generate metadata for the modal
      const fileType = getMatrixMessageType(file.type);
      const metadata = {
        fileName: file.name,
        mimeType: file.type,
        fileType,
      };
      setAttachmentMetadata(metadata);

      // Check if this generation was aborted
      if (controller.signal.aborted) return;

      // Generate thumbnail if applicable
      const thumbnailResult = await generateThumbnail(file);
      
      // Check if this generation was aborted after thumbnail generation
      if (controller.signal.aborted) return;

      // Set the generated thumbnail data
      if (thumbnailResult) {
        setAttachmentThumbnailBlob(thumbnailResult.thumbnail);
        setAttachmentThumbnailResult(thumbnailResult);
      } else {
        setAttachmentThumbnailBlob(null);
        setAttachmentThumbnailResult(null);
      }
    } catch (error) {
      // Check if this was an abort (not a real error)
      if (controller.signal.aborted) return;
      
      logger.warn('Failed to generate thumbnail for preview:', error);
      // Set metadata even if thumbnail generation fails
      const fileType = getMatrixMessageType(file.type);
      setAttachmentMetadata({
        fileName: file.name,
        mimeType: file.type,
        fileType,
      });
      setAttachmentThumbnailBlob(null);
      setAttachmentThumbnailResult(null);
    } finally {
      setIsGeneratingThumbnail(false);
      // Clear the controller if it's still the current one
      if (thumbnailAbortController.current === controller) {
        thumbnailAbortController.current = null;
      }
    }
  }, []);

  const clearAttachmentData = useCallback(() => {
    // Cancel any ongoing thumbnail generation
    if (thumbnailAbortController.current) {
      thumbnailAbortController.current.abort();
      thumbnailAbortController.current = null;
    }
    
    setAttachmentThumbnailBlob(null);
    setAttachmentThumbnailResult(null);
    setIsGeneratingThumbnail(false);
    setAttachmentMetadata(null);
    onClearAttachment();
  }, [onClearAttachment]);

  // --- Effects ---

  // Reset title when switching to edit mode or changing rooms
  useEffect(() => {
    if (isEditing) {
      setEditedTitle(name);
      setEditedTopic(topic || '');
    }
  }, [isEditing, name, topic, setEditedTopic]);

  // Reset specific detail views when room changes
  useEffect(() => {
    setIsReactionDetailsOpen(false);
    setReactionDetailsData(null);
    setIsContactDetailsOpen(false);
    setCurrentContactId(null);
    setIsMessageDetailsOpen(false);
    setCurrentMessageData(null);
    setIsEditHistoryOpen(false);
    setEditHistoryMessage(null);
    setIsRoomSettingsOpen(false);
  }, [roomId]);

  // Generate thumbnail when attachment changes
  useEffect(() => {
    generateAttachmentThumbnail(attachment);
  }, [attachment, generateAttachmentThumbnail]);

  const handleCancelClick = useCallback(() => {
    showConfirm(
      'Discard Changes?',
      'You have unsaved changes that will be lost.',
      () => {
        resetToOriginal();
        closeEdit();
      },
    );
  }, [showConfirm, closeEdit, resetToOriginal]);

  const handleSendMessage = useCallback(async (content) => {
    // The content is now prepared by the Messages component.
    // We just need to ensure there's a body to send.
    if (!content || !content.body || !content.body.trim()) return false;

    try {
      const result = await sendMessage(content, { sendReadReceipt: false });
      onSetMessage(''); // Use context's setter
      onMessageSent();
      return result; // Return the result which includes the event_id
    } catch (error) {
      logger.error('Failed to send message:', error);
      showError('Failed to Send Message', `Could not send the message: ${error.message}.`);
      return false;
    }
  }, [sendMessage, onSetMessage, onMessageSent, showError]);

  const handleDeleteMessage = useCallback(async (messageId, reason) => {
    await deleteMessage(messageId, reason);
  }, [deleteMessage]);

  const handleSendAttachment = useCallback(async (file, caption) => {
    const controller = new AbortController();

    try {
      const options = {
        abortController: controller,
        thumbnailResult: attachmentThumbnailResult,
        onProgressUpdate: setUploadProgress,
        replyEventId: replyingTo ? replyingTo.id : null, // Pass replyEventId if it exists
      };

      startUpload(controller);
      setUploadProgress(0); // Start at 0% (preparing)

      // Pass the abort controller, pre-generated thumbnail, and progress callback
      await sendAttachment(file, caption, options);

      return true;
    } catch (error) {
      if (error.name === 'AbortError') {
        return false;
      }

      logger.error('Failed to send attachment:', error);
      // Check for 413 Payload Too Large error
      if (error.httpStatus === 413) {
        showError(t('ChatView.attachmentFailedErrorTitle'), `File was over the maximum size of ${MAX_FILE_SIZE_MB}MB`);
      } else {
        showError(t('ChatView.attachmentFailedErrorTitle'), `Could not send the attachment: ${error.message}.`);
      }
      return false;
    } finally {
      stopUpload();
      setUploadProgress(-1);
    }
  }, [t, sendAttachment, showError, startUpload, stopUpload, setUploadProgress, attachmentThumbnailResult, replyingTo]);

  const handleSendAttachmentClick = useCallback(async (caption) => {
    const result = await handleSendAttachment(attachment, caption);
    if (result !== false) {
      clearAttachmentData();
      // If it was a reply, clear the reply state
      if (replyingTo) cancelReply();
    }
    return result;
  }, [handleSendAttachment, attachment, clearAttachmentData, replyingTo, cancelReply]);

  const handleChatDetailsToggle = useCallback(() => {
    setDetailsPanelOpen(!isDetailsPanelOpen);
  }, [isDetailsPanelOpen]);

  const handleReactionDetailsClose = useCallback(() => {
    setIsReactionDetailsOpen(false);
    setReactionDetailsData(null);
  }, []);

  const handleMessageDetailsClose = useCallback(() => {
    setIsMessageDetailsOpen(false);
    setCurrentMessageData(null);
  }, []);

  const handleEditHistoryClose = useCallback(() => {
    setIsEditHistoryOpen(false);
    setEditHistoryMessage(null);
  }, []);

  const handleAdvancedSettingsOpen = useCallback(() => {
    setIsRoomSettingsOpen(true);
  }, []);

  const handleAdvancedSettingsClose = useCallback(() => {
    setIsRoomSettingsOpen(false);
  }, []);

  const handleReactionDetailsOpen = useCallback((emoji, reactors, messageId, allReactions) => {
    // Close other detail views
    setIsContactDetailsOpen(false);
    setCurrentContactId(null);
    setIsMessageDetailsOpen(false);
    setCurrentMessageData(null);
    setIsEditHistoryOpen(false);
    setEditHistoryMessage(null);
    
    // Open reaction details
    setReactionDetailsData({ emoji, reactors, messageId, allReactions });
    setIsReactionDetailsOpen(true);

    // Also ensure the parent details panel is visible otherwise this view is hidden
    setDetailsPanelOpen(true);
  }, []);

  const handleContactClick = useCallback((contactId) => {
    if (contactId) {
      handleReactionDetailsClose();
      handleEditHistoryClose();
      handleMessageDetailsClose();
      setIsContactDetailsOpen(true);
      setCurrentContactId(contactId);
    }
  }, [handleReactionDetailsClose, handleEditHistoryClose, handleMessageDetailsClose]);

  const handleContactClose = useCallback(() => {
    setIsContactDetailsOpen(false);
    setCurrentContactId(null);
    // If we navigated to contact details from ReactionDetails, reopen ReactionDetails
    if (reactionDetailsData) {
      setIsReactionDetailsOpen(true);
    }
    // If we navigated to contact details from MessageDetails, reopen MessageDetails
    if (currentMessageData) {
      setIsMessageDetailsOpen(true);
    }
    // If we navigated to contact details from EditHistory, reopen EditHistory
    if (editHistoryMessage) {
      setIsEditHistoryOpen(true);
    }
  }, [reactionDetailsData, currentMessageData, editHistoryMessage]);

  const onFocusMessage = useCallback((message) => {
    if (!message || !messagesScrollRef.current) return;

    const messageId = message.getId ? message.getId() : message.eventId;
    if (!messageId) return;

    const element = messagesScrollRef.current.querySelector(`[data-event-id="${messageId}"]`);

    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Add a class to trigger the flash animation
      element.classList.add('message-item--flashed');

      // Remove the class after the animation completes
      setTimeout(() => {
        element.classList.remove('message-item--flashed');
      }, 1500); // Must match the animation duration in CSS
    }
  }, []);

  const handleMessageDetailsOpen = useCallback((message) => {
    // Close other detail views
    setIsReactionDetailsOpen(false);
    setReactionDetailsData(null);
    setIsContactDetailsOpen(false);
    setCurrentContactId(null);
    setIsEditHistoryOpen(false);
    setEditHistoryMessage(null);
    
    // Open message details
    setCurrentMessageData(message);
    setIsMessageDetailsOpen(true);
    
    // Also ensure the parent details panel is visible otherwise this view is hidden
    setDetailsPanelOpen(true);
  }, []);

  // Store previous sidebar state so it can be restored after closing edit history
  const prevSidebarState = useRef({
    isReactionDetailsOpen: false,
    reactionDetailsData: null,
    isContactDetailsOpen: false,
    currentContactId: null,
    isMessageDetailsOpen: false,
    currentMessageData: null,
  });

  const handleEditHistoryOpen = useCallback((message) => {
    // If clicking the same message, toggle off edit history and restore previous sidebar state
    if (isEditHistoryOpen && editHistoryMessage && editHistoryMessage.getId && message.getId && editHistoryMessage.getId() === message.getId()) {
      setIsEditHistoryOpen(false);
      setEditHistoryMessage(null);
      // Restore previous sidebar state
      setIsReactionDetailsOpen(prevSidebarState.current.isReactionDetailsOpen);
      setReactionDetailsData(prevSidebarState.current.reactionDetailsData);
      setIsContactDetailsOpen(prevSidebarState.current.isContactDetailsOpen);
      setCurrentContactId(prevSidebarState.current.currentContactId);
      setIsMessageDetailsOpen(prevSidebarState.current.isMessageDetailsOpen);
      setCurrentMessageData(prevSidebarState.current.currentMessageData);
      return;
    }

    // Save current sidebar state before opening edit history
    prevSidebarState.current = {
      isReactionDetailsOpen,
      reactionDetailsData,
      isContactDetailsOpen,
      currentContactId,
      isMessageDetailsOpen,
      currentMessageData,
    };

    // Only close other detail views, but do not clear their state
    setIsReactionDetailsOpen(false);
    setIsContactDetailsOpen(false);
    setIsMessageDetailsOpen(false);

    // Open edit history
    setEditHistoryMessage(message);
    setIsEditHistoryOpen(true);

    // Also ensure the parent details panel is visible otherwise this view is hidden
    setDetailsPanelOpen(true);
  }, [isEditHistoryOpen, editHistoryMessage, isReactionDetailsOpen, reactionDetailsData, isContactDetailsOpen, currentContactId, isMessageDetailsOpen, currentMessageData]);

  const handleRoomSave = async () => {
    // Check if there are new users being invited
    const newInvites = getNewInvites();
    
    if (newInvites.length > 0) {
      // Show confirmation modal for new invites
      showConfirm(
        'Invite New Users',
        'Please note that only legitimized users can be invited.',
        async () => {
          // User confirmed - proceed with save
          try {
            await saveRoomEdits(editedTitle);
            closeEdit();
          } catch (error) {
            showError('Failed to Save Changes', error.message);
          }
        },
        () => {
          // User cancelled - do nothing, just close the modal
        },
      );
    } else {
      // No new invites, proceed directly
      try {
        await saveRoomEdits(editedTitle);
        closeEdit();
      } catch (error) {
        showError('Failed to Save Changes', error.message);
      }
    }
  };

  const handleLeaveRoom = async () => {
    if (roomExists) {
      try {
        await leaveRoom();
        // Use the custom callback if provided, otherwise default to conversations
        if (onLeaveRoom) {
          onLeaveRoom();
        } else {
          navigate('/conversations');
        }
      } catch (error) {
        showError(
          'Failed to Leave Room',
          `Could not leave the room: ${error.message}. Please try again.`,
        );
      }
    }
  };

  // --- Render Logic ---

  // Combine toggle state with responsive width constraint
  const shouldShowDetailsPanel = isDetailsPanelOpen && isWideEnoughForDetails && !isEditing;

  if (isRoomLoading || !roomExists) {
    return <div className="chatview-blank" ref={chatViewRef} />;
  }

  return (
    <div className="chatview" ref={chatViewRef}>
      <div className={`chatview-top-bar-container ${(isEditing) ? 'edit' : ''}`}>
        <TopBar
          title={(isEditing) ? editedTitle : name}
          setTitle={(isEditing) ? setEditedTitle : undefined}
          topic={(isEditing) ? editedTopic : (topic || '')}
          setTopic={(isEditing) ? setEditedTopic : undefined}
          defaultTitle={(isEditing) ? getDefaultTitle() : ''}
          defaultTopic={(isEditing) ? (topic || '') : ''}
          isChatDetailsHidden={!isDetailsPanelOpen}
          onChatDetailsToggle={handleChatDetailsToggle}
          isEditMode={isEditing}
          canRename={canRename}
          canEditTopic={canRename}
          canSetRoomAvatar={canSetRoomAvatar}
          onSaveClick={handleRoomSave}
          onCancelClick={handleCancelClick}
          uploadRoomAvatar={(isEditing) ? uploadRoomAvatar : undefined}
          roomId={roomId}
          roomName={name}
          isEncrypted={isEncrypted}
          // Avatar state from useRoomEdit (computed automatically)
          selectedAvatarOption={selectedAvatarOption}
          selectedAvatarFile={selectedAvatarFile}
          avatarUrls={avatarUrls}
          defaultAvatarUrls={defaultAvatarUrls}
          hasAvatarChanged={hasAvatarChanged}
          // Avatar selection functions
          selectDefaultAvatar={selectDefaultAvatar}
          selectPresetAvatar={selectPresetAvatar}
          selectCustomAvatar={selectCustomAvatar}
        />
      </div>
      <div className={`chatview-content ${(isEditing) ? 'edit' : ''}`}>
        <div className={`chatview-messages-container ${shouldShowDetailsPanel || isEditing ? '' : 'fill'}`}>
          {/* This container will hold the modals, positioned on top of the Messages component */}
          <div id="modal-portal-container" />

          <Messages
            ref={messagesScrollRef}
            client={client}
            roomId={roomId}
            messages={messageGroups}
            typingText={typingText}
            placeholder={t('ChatViewFooter.typeMessagePlaceholder')}
            disabled={isEditing}
            value={currentMessage}
            onSetMessage={onSetMessage}
            onSendMessage={handleSendMessage} // This now expects a content object
            onSendAttachment={handleSendAttachment}
            onDeleteMessage={handleDeleteMessage}
            attachment={attachment}
            onTriggerFileSelect={onTriggerFileSelect}
            onClearAttachment={onClearAttachment}
            // Timeline pagination props
            isLoadingOlder={isLoadingOlder}
            isLoadingNewer={isLoadingNewer}
            canPaginateBackward={canPaginateBackward}
            canPaginateForward={canPaginateForward}
            windowPosition={windowPosition}
            isFillingViewport={isFillingViewport}
            onPaginateBackward={paginateBackward}
            onPaginateForward={paginateForward}
            onReactionDetailsOpen={handleReactionDetailsOpen}
            onMessageDetailsOpen={handleMessageDetailsOpen}
            onFocusMessage={onFocusMessage} // Pass the new handler down
            onEditHistoryOpen={handleEditHistoryOpen}
            onContactClick={handleContactClick}
            selectedMessageId={currentMessageData?.getId?.() || currentMessageData?.eventId || null}
            readMarkerId={readMarkerId}
            setReadMarker={setReadMarker}
            unreadCount={unreadCount}
          />
          {attachment && !isEditing && (
            <div
              className="chatview-attachment-background"
              onClick={clearAttachmentData}
            >
              <div
                className="chatview-attachment-container"
                onClick={(e) => e.stopPropagation()}
              >
                <SendAttachmentModal
                  thumbnail={attachmentThumbnailBlob}
                  fileName={attachmentMetadata?.fileName || ''}
                  mimeType={attachmentMetadata?.mimeType || ''}
                  fileType={attachmentMetadata?.fileType || 'm.file'}
                  onSendClick={handleSendAttachmentClick}
                  onCancelClick={clearAttachmentData}
                  isUploading={isUploading}
                  uploadProgress={uploadProgress}
                  isGeneratingThumbnail={isGeneratingThumbnail}
                  onCancelUpload={onCancelUpload}
                />
              </div>
            </div>
          )}
        </div>
        {
          isEditing && (
            <div className="chatview-details-container">
              <div className="chatview-details-edit-header">
        {canRename && (
        <>
          <label htmlFor="topic-input" className="chatview-details-edit-topic-label">
            {t('ChatView.topicLabel', 'Topic:')}
          </label>
          <input
            id="topic-input"
            type="text"
            className="chatview-details-edit-topic-input"
            value={editedTopic ?? topic ?? ''}
            onChange={(e) => setEditedTopic(e.target.value)}
            placeholder={t('ChatView.topicPlaceholder', 'Add a topic...')}
            maxLength={200}
            autoFocus
          />
          <div    className="chatview-details-edit-topic-charcount">
          {t('ChatView.topicRemainingCharCount', { characterCount: 200 - (editedTopic?.length || 0) })}
          </div>
        </>
      )}
      {!canRename && (editedTopic || topic) && (
        <>
          <div className="chatview-details-edit-topic-label">
            {t('ChatView.topicLabel', 'Topic:')}
          </div>
          <div className="chatview-details-edit-topic">{editedTopic || topic}</div>
        </>
      )}
      </div>
            <div className="chatview-details-edit-contacts">

              <ContactsSelector
                hasInvitePermissions={canInvite}
                hasRemovePermissions={canRemove}
                hasRenamePermissions={canRename}
                hasAvatarPermissions={canSetRoomAvatar}
                originalContacts={members}
                selectedContacts={selectedContacts}
                onRemoveContact={removeContact}
                onToggleContact={toggleContact}
                maxSelections={null}
                additionalUsers={externalUsers}
              />
              </div>
            </div>
          )
        }
        {
          shouldShowDetailsPanel && (
            <div className="chatview-details-container">
              {isReactionDetailsOpen ? (
                <ReactionDetails
                  emoji={reactionDetailsData?.emoji}
                  reactors={reactionDetailsData?.reactors || []}
                  messageId={reactionDetailsData?.messageId}
                  allReactions={reactionDetailsData?.allReactions}
                  members={members}
                  onClose={handleReactionDetailsClose}
                  onContactClick={handleContactClick}
                />
              ) : isContactDetailsOpen ? (
                <ContactDetailsSidebar
                  contact={members.find(m => m.id === currentContactId)}
                  onClose={handleContactClose}
                />
              ) : isMessageDetailsOpen ? (
                <MessageDetails
                  message={currentMessageData}
                  members={members}
                  roomId={roomId}
                  onClose={handleMessageDetailsClose}
                  onContactClick={handleContactClick}
                />
              ) : isEditHistoryOpen ? (
                <EditHistory
                  message={editHistoryMessage}
                  onClose={handleEditHistoryClose}
                />
              ) : (
                <Details
                  id={roomId}
                  title={name}
                  topic={topic}
                  avatars={roomAvatars}
                  participants={members}
                  onEditClick={startEdit}
                  onLeaveRoom={handleLeaveRoom}
                  onAdvancedSettingsClick={handleAdvancedSettingsOpen}
                  canEdit={canEdit}
                  canConfigure={canConfigure}
                  onClose={handleChatDetailsToggle}
                  onContactClick={handleContactClick}
                />
              )}
            </div>
          )
        }
        {isRoomSettingsOpen && (
          <div className="chatview-room-settings-overlay">
            <RoomSettings
              roomId={roomId}
              onClose={handleAdvancedSettingsClose}
            />
          </div>
        )}
      </div>
    </div>
  );
};

ChatViewContent.propTypes = {
  isEditing: PropTypes.bool,
  startEdit: PropTypes.func,
  closeEdit: PropTypes.func,
  startUpload: PropTypes.func,
  stopUpload: PropTypes.func,
  isUploading: PropTypes.bool,
  uploadProgress: PropTypes.number,
  setUploadProgress: PropTypes.func,
  onCancelUpload: PropTypes.func,
  attachment: PropTypes.object,
  onTriggerFileSelect: PropTypes.func,
  onClearAttachment: PropTypes.func,
  onLeaveRoom: PropTypes.func,
  isScreenWideEnoughForDetails: PropTypes.bool,
};

const ChatView = ({ roomId, ...props }) => {
  if (!roomId) {
    return <div className="chatview-blank" />;
  }

  return (
    // RoomProvider provides the core room data.
    // MessageDraftsProvider sits inside it to manage draft state for that room.
    <RoomProvider roomId={roomId}>
      <MessageDraftsProvider roomId={roomId}>
        <ChatViewContent {...props} roomId={roomId} />
      </MessageDraftsProvider>
    </RoomProvider>
  );
};

ChatView.propTypes = {
  roomId: PropTypes.string,
};

export default ChatView;
